/**
 * The message protocol exchanged between the Captello capture webview (the iframe)
 * and its host page.
 *
 * Wire format (this is the contract — match it exactly):
 * - Every message is a JSON **string**. The webview sends outbound messages with
 *   `JSON.stringify(message)` and reads inbound messages with `JSON.parse(event.data)`.
 *   A host that posts a raw object instead of a string will be ignored, because the
 *   webview's parser produces a non-object and bails.
 * - Every message is an object with a `type` discriminator. Inbound and outbound
 *   types are disjoint string enums.
 *
 * Direction is named from the **webview's** point of view:
 * - {@link OutboundMessageType}: webview → host (the host listens for these).
 * - {@link InboundMessageType}: host → webview (the host sends these).
 */

import type { VisibleSubmissionDataItem } from "./submission-data";

/* ------------------------------------------------------------------ *
 * Outbound: webview → host
 * ------------------------------------------------------------------ */

/** Message `type` values the webview emits to its host. */
export enum OutboundMessageType {
    /** The form finished loading and rendering. Safe to interact with it after this. */
    FormLoadComplete = "form_load_complete",
    /** A user-facing error occurred; `data` is the translated, display-ready message. */
    FormErrorMessage = "form_error_message",
    /**
     * Emitted for embedded forms instead of submitting directly: `data` is the full
     * submission body for the host to persist/forward.
     */
    SubmissionBody = "submission_body",
    /** The form was submitted successfully. `action` indicates whether it was a new submission or an update. */
    FormSubmitSuccess = "form_submit_success",
    /** Connexions: the host should perform the profile redirect (embed mode). */
    ConnexionsProfileRedirect = "connexions_profile_redirect",
    /** Connexions: the host should trigger the vCard download (embed mode). */
    ConnexionsDownloadVcard = "connexions_download_vcard",
    /**
     * The user pressed the transcribe button (shown only when the embed URL enables
     * it via `show_transcribe_button`): the host should transcribe the request's
     * scan images and answer with an inbound
     * {@link InboundMessageType.TranscribeScannerResult}.
     */
    TranscribeScannerRequest = "transcribe_scanner_request",
    /**
     * Answer to an inbound {@link InboundMessageType.SubmissionDataRequest}: the form's
     * current values as a {@link SubmissionBody}, unvalidated. Echoes the request's
     * `request_id`.
     */
    SubmissionDataResult = "submission_data_result",
}

/**
 * Opaque submission payload carried by {@link OutboundMessageType.SubmissionBody}.
 *
 * This mirrors the webview's internal `FormSubmission` model. It is intentionally
 * typed as an open record here so the SDK stays decoupled from the app's full model
 * graph; the documented fields below are stable, the rest are passed through as-is.
 * Host code that needs the deep element-value types should treat `data` as untyped
 * and key it by element id (e.g. `"element_12"`, `"element_12_3"`).
 */
export interface SubmissionBody {
    id: number;
    form_id: number;
    prospect_id: number;
    email: string;
    first_name: string;
    last_name: string;
    full_name: string;
    company: string;
    phone: string;
    /** Submitted values keyed by element id / sub-element id. */
    data: Record<string, unknown>;
    /**
     * Visible, filled elements ready to render as key/value rows — one item per
     * element, discriminated by `element_type` (narrow on it for a precisely-typed
     * `element_value`). See {@link VisibleSubmissionDataItem}. May be absent on older
     * webview builds.
     */
    visible_submissions_data?: VisibleSubmissionDataItem[];
    submission_date: string;
    /** Query-string params the webview was loaded with, echoed back on submit. */
    query_parameters?: Record<string, string>;
    /** Additional fields from the webview's submission model are passed through verbatim. */
    [key: string]: unknown;
}

/**
 * Submitted values flat-keyed by element / sub-element id (e.g. `"element_12"`, `"element_12_3"`).
 *
 * This is the webview's own internal shape rather than the submissions API's: it is what a
 * received {@link SubmissionBody} carries, and what the backend stores verbatim for a draft and
 * hands back unchanged — hence the name. Either can be passed to
 * {@link SubmissionPrefill.data} as-is, so an `onSubmissionBody` payload round-trips.
 *
 * Contrast {@link SubmissionPrefillDataItem}, the array shape the submissions API returns.
 */
export type DraftSubmissionData = Record<string, unknown>;

/**
 * One submitted value in {@link SubmissionPrefill.data}. Mirrors the submissions API's
 * `SubmissionDataResponse` shape, so a `submission.data` array fetched from that API can
 * be passed straight through as-is.
 */
export interface SubmissionPrefillDataItem {
    element_id: string;
    element_title: string;
    value: string;
    /** Present only when the element has sub-elements (e.g. a simple name or address). */
    value_splitted?: Record<string, string>;
}

/**
 * Loose submission shape accepted when **pre-filling** the form (host → webview).
 *
 * Distinct from {@link SubmissionBody} in that every field is optional — assemble a partial
 * object from your own data rather than populating a whole body.
 *
 * `data` accepts either shape a host is likely to be holding, and the webview normalizes
 * whichever it receives:
 * - {@link SubmissionPrefillDataItem}`[]` — one entry per element, as the submissions API
 *   returns it for a submitted submission. Pass a fetched `submission.data` array straight
 *   through.
 * - {@link DraftSubmissionData} — values flat-keyed by element / sub-element id. This is the
 *   shape a received {@link SubmissionBody} carries, and the shape a draft is stored and
 *   returned in, so `onSubmissionBody` payloads round-trip directly.
 */
export interface SubmissionPrefill {
    /**
     * Submitted values, as either the submissions API's array (see
     * {@link SubmissionPrefillDataItem}) or a flat {@link DraftSubmissionData} record.
     */
    data?: SubmissionPrefillDataItem[] | DraftSubmissionData;
    [key: string]: unknown;
}

interface FormLoadCompleteMessage {
    type: OutboundMessageType.FormLoadComplete;
}
interface FormSubmitSuccessMessage {
    type: OutboundMessageType.FormSubmitSuccess;
    action: "create" | "update";
}
interface FormErrorMessageMessage {
    type: OutboundMessageType.FormErrorMessage;
    /** Translated, display-ready error text. */
    data: string;
}
interface SubmissionBodyMessage {
    type: OutboundMessageType.SubmissionBody;
    data: SubmissionBody;
}
interface ConnexionsProfileRedirectMessage {
    type: OutboundMessageType.ConnexionsProfileRedirect;
}
interface ConnexionsDownloadVcardMessage {
    type: OutboundMessageType.ConnexionsDownloadVcard;
}

/**
 * Webview → host: the user pressed the transcribe button. Answer with a
 * {@link TranscribeScannerResultMessage} (see
 * {@link CaptelloWebview.onTranscribeScannerRequest} for the handler that does this
 * for you). The webview shows a pending state on the button and gives up with a
 * user-facing error if no result arrives in time.
 *
 * Host processing is a convention, not a one-off: future operations get their own
 * `*_request` / `*_result` message pairs shaped like this one.
 */
export interface TranscribeScannerRequestMessage {
    type: OutboundMessageType.TranscribeScannerRequest;
    /** Correlation id — echo it back verbatim in the result. */
    request_id: string;
    /** The scanner (badge/barcode) element whose scan should be transcribed (e.g. `"element_6"`). */
    element_id: string;
    /** Public URLs of the uploaded scan images (the scanner element's image value). */
    image_urls: string[];
    /** The draft submission token the webview was loaded with, when it has one. */
    draft_submission_token?: string;
}

/**
 * Webview → host: the answer to a {@link SubmissionDataRequestMessage}. Carries `data` on
 * success or `error` (display-ready text) when the body could not be built — e.g. the form
 * has not loaded, or a file failed to upload.
 */
export interface SubmissionDataResultMessage {
    type: OutboundMessageType.SubmissionDataResult;
    /** The `request_id` of the request being answered, verbatim. */
    request_id: string;
    /** The form's current values, shaped like a `submission_body` (success). */
    data?: SubmissionBody;
    /** Display-ready error text (failure) — `data` is unset. */
    error?: string;
}

/** Discriminated union of every message the webview can emit to its host. */
export type OutboundMessage =
    | FormLoadCompleteMessage
    | FormSubmitSuccessMessage
    | FormErrorMessageMessage
    | SubmissionBodyMessage
    | ConnexionsProfileRedirectMessage
    | ConnexionsDownloadVcardMessage
    | TranscribeScannerRequestMessage
    | SubmissionDataResultMessage;

/** Maps each outbound `type` to its full message shape (used by the client's `.on`). */
export type OutboundMessageMap = {
    [M in OutboundMessage as M["type"]]: M;
};

/* ------------------------------------------------------------------ *
 * Inbound: host → webview
 * ------------------------------------------------------------------ */

/** Message `type` values the host sends into the webview. */
export enum InboundMessageType {
    /** Programmatically trigger form submission (as if the user pressed submit). */
    Submit = "submit_form",
    /** Reset the form, clearing all entered values. */
    Reset = "reset_form",
    /** Pre-fill the form with existing data. */
    FormPrefill = "form_prefill",
    /** Switch the current submission into draft-update mode. */
    UpdateDraft = "update_draft",
    /** Run validation against a target field (or the whole form). */
    TriggerValidation = "trigger_validation",
    /** Answer to an outbound {@link OutboundMessageType.TranscribeScannerRequest}. */
    TranscribeScannerResult = "transcribe_scanner_result",
    /**
     * Ask for the form's current values without validating them. Answered with an
     * outbound {@link OutboundMessageType.SubmissionDataResult}.
     */
    SubmissionDataRequest = "submission_data_request",
}

/**
 * Shape selector for {@link InboundMessageType.FormPrefill} payloads.
 *
 * Vestigial: the webview keys only off the message `type` and reads `data` directly —
 * it never inspects `data_type`. Confirmed by mutation-testing the integration spec (a
 * deliberately wrong value changed nothing). Kept because it is part of the shipped wire
 * shape and older webview builds may still read it; `prefill()` sets it for you.
 */
export enum PrefillDataType {
    UlcSubmissionAndInfo = "ulc_submission_and_info",
}

/** Targets for {@link InboundMessageType.TriggerValidation}. */
export type ValidationTarget = "invitation_code" | "email" | "all";

/**
 * A single transcription field/value item for the `info` prefill array.
 *
 * The webview matches each item to a form element by `ll_field_unique_identifier`
 * alone (e.g. `"FirstName"`, `"Email"`); `ll_field_id` is catalog metadata and is not
 * used for matching, so it is accepted as either a number or a string. `value` is
 * typically a string but may be a boolean (e.g. the PII opt-out field).
 */
export interface PrefillInfoItem {
    ll_field_unique_identifier: string;
    ll_field_id?: string | number;
    value: string | boolean;
}

interface SubmitMessage {
    type: InboundMessageType.Submit;
    /**
     * Whether a refused submit is also shown as a toast in the form. The refusal is sent
     * as `form_error_message` regardless. Absent: the webview's default for the launcher.
     */
    show_error_in_form?: boolean;
}
interface ResetMessage {
    type: InboundMessageType.Reset;
}
interface UpdateDraftMessage {
    type: InboundMessageType.UpdateDraft;
}
interface TriggerValidationMessage {
    type: InboundMessageType.TriggerValidation;
    target: ValidationTarget;
}
interface PrefillMessage {
    type: InboundMessageType.FormPrefill;
    data_type: PrefillDataType.UlcSubmissionAndInfo;
    data: { submission?: SubmissionPrefill; info?: PrefillInfoItem[] };
}

/**
 * One transcribed field in {@link TranscribeScannerResultData.fields}.
 *
 * Semantically the same thing as a {@link PrefillInfoItem} but in the transcription
 * service's camelCase wire casing — `llFieldIdentifier` carries the same values as
 * `ll_field_unique_identifier` (e.g. `"FirstName"`, `"Email"`), and it is what the
 * webview matches form elements by. The webview ignores fields with an empty `value`
 * rather than blanking already-filled inputs.
 */
export interface TranscribedScannerField {
    /** Catalog field identifier the webview matches on, e.g. `"FirstName"`. */
    llFieldIdentifier: string;
    /** Catalog field id — metadata, not used for matching. */
    llFieldId?: number;
    /** Display name as the transcription service reports it. */
    llFieldNameName?: string;
    value: string;
}

/** Success payload of a {@link TranscribeScannerResultMessage}. */
export interface TranscribeScannerResultData {
    fields: TranscribedScannerField[];
    /** How the transcription was produced, e.g. `"ocr_transcription"`. */
    submissionType?: string;
}

/**
 * Host → webview: the answer to a {@link TranscribeScannerRequestMessage}. Send
 * `data` with the transcribed fields on success, or `error` (display-ready text) on
 * failure. `request_id` should echo the request's; the webview also accepts a result
 * without one while exactly one request is pending, for hosts that answer strictly
 * one at a time.
 */
export interface TranscribeScannerResultMessage {
    type: InboundMessageType.TranscribeScannerResult;
    /** The `request_id` of the request being answered, verbatim. */
    request_id?: string;
    /** Transcribed fields (success). */
    data?: TranscribeScannerResultData;
    /** Display-ready error text (failure) — leave `data` unset. */
    error?: string;
}

/**
 * Host → webview: ask for the form's current values as a {@link SubmissionBody}, without
 * validating them. The webview builds the body the way it does on submit — uploading any
 * pending files so they arrive as URLs — but skips every check and prompt, saves nothing,
 * and leaves the form as it is. Answered with a {@link SubmissionDataResultMessage}.
 */
export interface SubmissionDataRequestMessage {
    type: InboundMessageType.SubmissionDataRequest;
    /** Correlation id — the webview echoes it back in the result. */
    request_id: string;
}

/** Discriminated union of every message the host can send into the webview. */
export type InboundMessage =
    | SubmitMessage
    | ResetMessage
    | UpdateDraftMessage
    | TriggerValidationMessage
    | PrefillMessage
    | TranscribeScannerResultMessage
    | SubmissionDataRequestMessage;

/* ------------------------------------------------------------------ *
 * Runtime guards / parsing
 * ------------------------------------------------------------------ */

const OUTBOUND_TYPES: ReadonlySet<string> = new Set(Object.values(OutboundMessageType));

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Parses a raw `MessageEvent.data` value into a typed {@link OutboundMessage}, or
 * returns `null` if it is not a recognized Captello webview message.
 *
 * Accepts either a JSON string (the webview always sends strings) or an
 * already-parsed object, so it is robust to hosts/proxies that pre-parse.
 */
export function parseOutboundMessage(data: unknown): OutboundMessage | null {
    let value: unknown = data;
    if (typeof value === "string") {
        try {
            value = JSON.parse(value);
        } catch {
            return null;
        }
    }
    if (!isPlainObject(value)) return null;
    if (typeof value["type"] !== "string" || !OUTBOUND_TYPES.has(value["type"])) return null;
    return value as unknown as OutboundMessage;
}
